<script lang="ts">
    import { getMediaById } from '$lib/components/Blocks/_utils/getMediaById';
    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { cn } from '$lib/utils/cn';
    import { getResponseBgColor } from '$lib/utils/getResponseBgColor';

    import { fade } from 'svelte/transition';
    import CountUp from 'svelte-countup';

    import { DEFAULT_BOX, MOBILE_BREAKPOINT, TABLET_BREAKPOINT } from '../../constants';
    import { deleteIrrelevantImgixKeys } from '../_utils/helpers';
    import Transition from '../_wrappers/Transition.svelte';
    import Icon from '../Icon/Icon.View.svelte';
    import Image from '../Image/Image.View.svelte';
    import Text from '../Text/Text.View.svelte';

    import type { Box as BoxType } from '$lib/types/Components/SharedTypes';
    import type { TransitionEffect } from '$lib/types/Theme';

    export let id = '';
    export let wysiwygHtml: string | undefined = undefined;
    export let color = '#0A0A0A';
    export let link = '';
    export let src = '';
    export let srcType = 'image';
    export let icon = '';
    export let svg = '';
    export let platform = '';
    export let iconName = '';
    export let iconColor = '#0A0A0A';
    export let isAnimated = false;
    export let isResultVisible = false;
    export let jsonUrl: string | null = null;
    export let bgColor = 'transparent';
    export let imgBgColor = 'transparent';
    export let resultId = '';
    export let onClick: (resultId: string, id: string, link: string) => void;
    export let onMouseEnter: (resultId: string, id: string, link: string) => void;
    export let transitionEffect: TransitionEffect = 'none';
    export let transitionDelay = 0;
    export let responsePercentage: number | undefined = 0;
    export let mediaIds: string[] = [];

    const cleanImgSrc = deleteIrrelevantImgixKeys(src);

    const media = getMediaById(mediaIds.length > 0 ? mediaIds[0] : '');

    const handleClick = () => {
        // Prevent clicks after results are shown
        if (isResultVisible) {
            return;
        }

        onClick(resultId, id, link);
    };

    const handleKeyPress = (event: KeyboardEvent) => {
        event.preventDefault();

        if (event.key === 'Enter' || event.key === ' ') {
            handleClick();
        }
    };

    const handleMouseEnter = () => {
        onMouseEnter(resultId, id, link);
    };

    const getWidthByScreenSize = () => {
        const screenSize = window.innerWidth;

        if (screenSize < MOBILE_BREAKPOINT) {
            return 156;
        } else if (screenSize < TABLET_BREAKPOINT) {
            return 168;
        } else {
            return 212;
        }
    };

    const textBox: BoxType = {
        top: 'medium',
        bottom: 'medium',
        left: 'medium',
        right: 'medium',
        backgroundColor: bgColor,
        grow: true,
    };

    const choiceItemBorderRadiusClass = getBorderRadius('choiceItem');

    const responseBgColor = getResponseBgColor(bgColor);

    let resultBgWidth = 0;

    $: if (isResultVisible) {
        resultBgWidth = (responsePercentage ?? 0) * 100;
    }
</script>

<Transition effect={transitionEffect} delay={transitionDelay} fullHeight>
    <div
        class={cn(
            'fun-relative fun-z-10 fun-flex fun-h-full fun-transform fun-cursor-pointer fun-flex-col fun-overflow-hidden fun-transition fun-ease-out',
            'active:fun-scale-90 active:fun-duration-200 sm:fun-duration-500 sm:hover:fun-scale-105 sm:hover:fun-shadow-lg',
            choiceItemBorderRadiusClass,
        )}
        style="background-color: {imgBgColor};"
        on:click={handleClick}
        on:mouseenter={handleMouseEnter}
        on:touchstart={handleMouseEnter}
        on:keypress={handleKeyPress}
        role="button"
        tabindex="0"
    >
        <div
            class={cn(
                'fun-absolute fun-inset-0 fun-z-10 fun-size-full fun-transition-all fun-duration-700 [transition-timing-function:cubic-bezier(0.33,1,0.68,1)]',
                choiceItemBorderRadiusClass,
                responseBgColor,
            )}
            style="width: {resultBgWidth}%;"
        />

        <div class="fun-relative fun-aspect-[4/3]">
            {#if isResultVisible}
                <div
                    class="fun-font-inter fun-relative fun-z-10 fun-flex fun-size-full fun-items-center fun-justify-center fun-text-2xl fun-font-semibold fun-leading-8 fun-tracking-normal"
                    style="color: {color}"
                    in:fade
                >
                    <CountUp
                        initial={0}
                        value={((responsePercentage || 0) * 100).toFixed(0)}
                        duration={700}
                        step={1}
                        roundto={1}
                    />%
                </div>
            {:else if srcType === 'image'}
                <Image
                    src={cleanImgSrc}
                    size="answer"
                    fixedMaxWidth={getWidthByScreenSize()}
                    transitionEffect="none"
                    alt={media?.alt || media?.altDescription}
                />
            {:else}
                <div class="fun-flex fun-size-full fun-items-center fun-justify-center">
                    <Icon
                        box={DEFAULT_BOX}
                        {icon}
                        color={iconColor}
                        {svg}
                        {platform}
                        {iconName}
                        {isAnimated}
                        {jsonUrl}
                        size="answer"
                        transitionEffect="none"
                    />
                </div>
            {/if}
        </div>

        <Text
            {wysiwygHtml}
            {color}
            align="center"
            box={textBox}
            transitionEffect="none"
            isInMediaAnswer
        />
    </div>
</Transition>
